/**
 * Region-specific SEO pages. One data entry per region, rendered by
 * RegionLandingPage.jsx. Ranks for "restaurant POS [country/city]" long-tail
 * queries across US, Canada, UK, Middle East, and Africa.
 */
export const REGIONS = {
  'united-states': {
    slug: 'united-states',
    country: 'United States',
    countryShort: 'US',
    flag: '🇺🇸',
    currency: 'USD',
    languages: ['English', 'Spanish'],
    hreflang: 'en-US',
    metaTitle: 'Restaurant POS System in the United States — Cloud POS + AI Marketing | FlavorFeed',
    metaDescription: 'Cloud restaurant POS built for US restaurants. Integrates with Stripe, Helcim, Valor, and Clover. Multi-state tax compliance, Apple Pay/Google Pay ready, free forever tier.',
    heroTitle: 'The Cloud Restaurant POS Built for the United States',
    subtitle: 'From Manhattan diners to LA food trucks — FlavorFeed runs the modern US restaurant. Accept Apple Pay, automate your Instagram, manage multi-state chains.',
    heroBullets: [
      'Works in all 50 states — multi-state sales tax engine built in',
      'Integrates with Stripe, Helcim, Valor, Clover (pick any)',
      'Apple Pay, Google Pay, Tap-to-Pay on iPhone ready',
      'DoorDash, Uber Eats, Grubhub menu sync',
    ],
    cities: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia', 'San Antonio', 'San Diego', 'Dallas', 'San Jose', 'Austin', 'Miami', 'Seattle', 'Boston', 'Atlanta'],
    regulations: [
      'Multi-state sales tax: automatic rate lookup for all 50 states by ZIP code',
      'IRS-compliant Z-reports and end-of-day close',
      '1099-K payment reporting integration with Stripe, Helcim',
      'PCI DSS Level 1 compliance via payment processor partners',
      'ADA accessible customer-facing display',
    ],
    faqs: [
      {q: 'Does FlavorFeed handle multi-state sales tax?', a: 'Yes — FlavorFeed auto-looks up the correct sales tax rate for each order by ZIP code across all 50 US states, including combined state + county + city + special district rates. For online ordering, tax is calculated from delivery address.'},
      {q: 'Can I accept tips the American way — on-screen prompts at payment?', a: 'Yes. Configurable tip prompts (15%/18%/20%/22%/custom) appear on the customer-facing display after order total. Works on all supported payment terminals and Tap-to-Pay.'},
      {q: 'Does FlavorFeed integrate with QuickBooks?', a: 'Yes — A native QuickBooks Online integration is on our roadmap. Daily sales, tax, tip, and refund summaries can be exported for import into QuickBooks Online.'},
      {q: 'Can I run FlavorFeed in Spanish?', a: 'Yes — POS interface, receipts, and customer display all support Spanish. Toggle per-terminal or per-branch.'},
    ],
  },
  'canada': {
    slug: 'canada',
    country: 'Canada',
    countryShort: 'Canada',
    flag: '🇨🇦',
    currency: 'CAD',
    languages: ['English', 'French'],
    hreflang: 'en-CA',
    metaTitle: 'Restaurant POS System in Canada — GST/HST/PST Compliant Cloud POS | FlavorFeed',
    metaDescription: 'Cloud restaurant POS built for Canadian restaurants. Bilingual English/French, GST/HST/PST automatic, Interac debit, and Helcim (Canadian processor) integrated. Free forever tier.',
    heroTitle: 'The Cloud Restaurant POS Built for Canada',
    subtitle: 'From Toronto to Vancouver — FlavorFeed speaks English, French, handles GST/HST/PST/QST automatically, and integrates with Helcim (Calgary-based processor).',
    heroBullets: [
      'Bilingual English/French — receipts, menu, POS interface',
      'Automatic GST, HST, PST, QST tax handling per province',
      'Helcim integration (Canadian-owned processor, better rates than Stripe)',
      'Interac debit support via terminal partners',
    ],
    cities: ['Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Edmonton', 'Ottawa', 'Winnipeg', 'Quebec City', 'Hamilton', 'Halifax'],
    regulations: [
      'GST/HST/PST/QST automatic tax handling by province',
      'Bilingual (EN/FR) receipt requirement met by default',
      'CRA-compliant audit trail and transaction records',
      'Quebec WEB-1275 / MEV fiscal compliance roadmap',
    ],
    faqs: [
      {q: 'Is FlavorFeed bilingual for Quebec restaurants?', a: 'Yes — every receipt, POS screen, and customer-facing display is fully bilingual (English/French). Bilingual defaults designed for Quebec Language Law (Bill 96).'},
      {q: 'Which Canadian payment processor is cheapest?', a: 'For most Canadian restaurants, Helcim (Calgary-based, interchange-plus pricing) beats Stripe by ~0.4% on volume. FlavorFeed integrates both — pick whichever suits your volume.'},
      {q: 'Does FlavorFeed handle HST for Ontario or just GST?', a: 'Both. FlavorFeed auto-applies the correct combined rate per province — 13% HST in Ontario, 15% HST in Atlantic provinces, 5% GST + provincial tax in AB/BC/SK/MB/NT/NU/YT, 5% GST + 9.975% QST in Quebec.'},
      {q: 'Is FlavorFeed ready for Quebec MEV (cash register recording module) requirements?', a: 'FlavorFeed maintains compliant transaction journals. Full MEV device integration is on our Quebec-specific roadmap — contact us if this is a blocker.'},
    ],
  },
  'united-kingdom': {
    slug: 'united-kingdom',
    country: 'United Kingdom',
    countryShort: 'UK',
    flag: '🇬🇧',
    currency: 'GBP',
    languages: ['English'],
    hreflang: 'en-GB',
    metaTitle: 'Restaurant POS System in the UK — VAT-Compliant Cloud POS | FlavorFeed',
    metaDescription: 'Cloud restaurant POS for UK restaurants. HMRC Making Tax Digital ready, VAT 20%/5%/0% handling, Stripe UK integrated, contactless + Apple Pay + Google Pay. Free forever tier.',
    heroTitle: 'The Cloud Restaurant POS Built for the United Kingdom',
    subtitle: 'From London gastropubs to Edinburgh cafés — FlavorFeed handles VAT records for Making Tax Digital, plus contactless payments out of the box.',
    heroBullets: [
      'HMRC Making Tax Digital (MTD) ready',
      'VAT 20% / 5% / 0% / exempt handling, with receipt breakdowns',
      'Stripe UK integration — contactless, Apple Pay, Google Pay ready',
      'Multi-currency (GBP, EUR, USD) for tourist-heavy districts',
    ],
    cities: ['London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Edinburgh', 'Liverpool', 'Bristol', 'Cardiff', 'Belfast', 'Oxford', 'Cambridge'],
    regulations: [
      'HMRC Making Tax Digital (MTD) ready — digital VAT records',
      'VAT 20% / 5% / 0% / exempt per-item configuration',
      'GDPR-compliant customer data handling',
      'UKCA hardware certification for approved terminals',
    ],
    faqs: [
      {q: 'Is FlavorFeed MTD (Making Tax Digital) compliant?', a: 'FlavorFeed maintains digital VAT records in MTD format and exports VAT data for MTD-compatible bookkeeping (QuickBooks, Xero, Sage) used for quarterly VAT returns to HMRC.'},
      {q: 'Can I set different VAT rates per menu item (hot food 20% vs cold takeaway 0%)?', a: 'Yes — each menu item can be tagged with its VAT rate. FlavorFeed handles the standard 20%, reduced 5% (e.g., children\'s car seats), and zero-rate (takeaway cold food) automatically.'},
      {q: 'Does FlavorFeed integrate with UK payment processors?', a: 'Stripe UK is integrated and production-ready. Helcim UK, Valor UK, and Dojo integrations are on the roadmap — contact us if you need a specific UK processor.'},
    ],
  },
  'uae': {
    slug: 'uae',
    country: 'United Arab Emirates',
    countryShort: 'UAE',
    flag: '🇦🇪',
    currency: 'AED',
    languages: ['Arabic', 'English'],
    hreflang: 'en-AE',
    metaTitle: 'Restaurant POS System in UAE — Arabic Cloud POS, VAT Compliant | FlavorFeed',
    metaDescription: 'Cloud restaurant POS for UAE restaurants. Bilingual Arabic/English receipts, UAE 5% VAT compliant, Talabat + Deliveroo + Careem Food integrations, ADNOC/Emirates Pay. Dubai, Abu Dhabi ready.',
    heroTitle: 'The Cloud Restaurant POS Built for the UAE',
    subtitle: 'From Dubai Mall to Abu Dhabi Corniche — FlavorFeed speaks Arabic and English natively, handles UAE VAT, and syncs with Talabat, Deliveroo, and Careem Food.',
    heroBullets: [
      'Fully bilingual Arabic/English — RTL-native interface',
      'UAE 5% VAT invoice-level compliance',
      'Talabat, Deliveroo, Careem Food, Zomato menu sync',
      'Multi-branch support for UAE chains (Jumeirah, JBR, Marina, City Walk)',
    ],
    cities: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Al Ain', 'Umm Al Quwain'],
    regulations: [
      'UAE 5% VAT compliance with bilingual tax invoices',
      'FTA e-invoicing Phase 1 readiness (Arabic + English)',
      'Dubai Municipality food safety record support',
      'Bilingual (Arabic/English) receipt requirement met',
    ],
    faqs: [
      {q: 'Is FlavorFeed a ZATCA-style compliant POS for the UAE?', a: 'Yes — VAT invoices with both Arabic and English, QR codes, per-item tax breakdowns, and all required FTA data fields. UAE FTA e-invoicing Phase 1 ready.'},
      {q: 'Can FlavorFeed integrate with Talabat and Deliveroo?', a: 'Yes. Menu sync, order routing to KDS, and consolidated reporting across Talabat, Deliveroo, Careem Food, and Zomato — included in Pro tier.'},
      {q: 'Does FlavorFeed print bilingual receipts (Arabic + English)?', a: 'Yes — configurable per-branch or per-ticket. Arabic on top, English below; automatic RTL handling.'},
      {q: 'Which payment processors work in the UAE?', a: 'Stripe UAE, Network International, and Telr are supported. Contact us for specific ADNOC Pay or Emirates Digital Wallet integrations.'},
    ],
  },
  'saudi-arabia': {
    slug: 'saudi-arabia',
    country: 'Saudi Arabia',
    countryShort: 'KSA',
    flag: '🇸🇦',
    currency: 'SAR',
    languages: ['Arabic', 'English'],
    hreflang: 'ar-SA',
    metaTitle: 'Restaurant POS System in Saudi Arabia — ZATCA Phase 2 Compliant | FlavorFeed',
    metaDescription: 'Cloud restaurant POS built for Saudi Arabia. ZATCA Phase 2 e-invoicing ready, bilingual Arabic/English, Jahez + ToYou + HungerStation integrated. Used by restaurants in Riyadh, Jeddah, Mecca, Dammam.',
    heroTitle: 'ZATCA Phase 2 Ready — Built for Saudi Arabia',
    subtitle: 'From Riyadh to Jeddah — FlavorFeed is the cloud restaurant POS that ZATCA-compliant from day one, fully bilingual, and integrated with Jahez, ToYou, HungerStation.',
    heroBullets: [
      'Built to the ZATCA Phase 2 (Fatoora) specification — e-invoicing + QR codes',
      'Fully bilingual Arabic/English with RTL support',
      'Jahez, ToYou, HungerStation, Zomato, Mrsool menu sync',
      '15% VAT automatic with Fatoora portal integration',
    ],
    cities: ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam', 'Khobar', 'Taif', 'Abha', 'Tabuk', 'Buraidah', 'Hail', 'Najran'],
    regulations: [
      'ZATCA Phase 2 (Fatoora) e-invoicing — cryptographic stamp + QR',
      '15% VAT per-invoice breakdown, bilingual Arabic/English',
      'Ministry of Commerce CR (Commercial Registration) integration',
      'SFDA food safety and labeling support',
    ],
    faqs: [
      {q: 'Is FlavorFeed ZATCA Phase 2 certified?', a: 'FlavorFeed is built to the ZATCA Phase 2 (Fatoora) specification — cryptographic invoice stamping, QR code embedding, XML generation, and automatic submission to the Fatoora portal. We support all ZATCA Phase 2 requirements out of the box.'},
      {q: 'Does FlavorFeed work with Jahez and HungerStation?', a: 'Yes — full menu sync, order routing into the KDS, and consolidated reporting across Jahez, ToYou, HungerStation, Zomato, and Mrsool. Orders appear in your POS automatically.'},
      {q: 'Can I run FlavorFeed entirely in Arabic?', a: 'Yes — the POS terminal, admin dashboard, customer display, digital menu, and receipts are all natively Arabic with right-to-left (RTL) layout. Each user/branch can be set to Arabic independently.'},
      {q: 'Does FlavorFeed handle the 15% VAT calculation automatically?', a: 'Yes. Every item is configured with its VAT treatment (standard/zero-rated/exempt), and every invoice shows the correct 15% breakdown in both Arabic and English, with ZATCA-compliant QR code.'},
    ],
  },
  'africa': {
    slug: 'africa',
    country: 'Africa',
    countryShort: 'Africa',
    flag: '🌍',
    currency: 'Multi',
    languages: ['English', 'French', 'Arabic', 'Swahili'],
    hreflang: 'en',
    metaTitle: 'Restaurant POS System for Africa — Cloud POS for Nigeria, Kenya, South Africa, Egypt | FlavorFeed',
    metaDescription: 'Cloud restaurant POS for African restaurants. Works in Nigeria, Kenya, South Africa, Egypt, Ghana, Morocco. Multi-currency, mobile money (M-Pesa, MTN), offline mode for unreliable internet. Free forever tier.',
    heroTitle: 'The Cloud Restaurant POS Built for Africa',
    subtitle: 'From Lagos to Nairobi, Cairo to Cape Town — FlavorFeed handles multi-currency, mobile money (M-Pesa, MTN MoMo), and unreliable internet with full offline mode.',
    heroBullets: [
      'Offline mode — keep selling when internet drops (syncs when back)',
      'Multi-currency: NGN, KES, ZAR, EGP, GHS, MAD, XOF, XAF, USD, EUR',
      'Mobile money ready: M-Pesa (Kenya), MTN MoMo, Airtel Money, Flutterwave',
      'Arabic (Egypt, Morocco), French (West Africa), English, Swahili',
    ],
    cities: ['Lagos', 'Cairo', 'Nairobi', 'Johannesburg', 'Cape Town', 'Accra', 'Casablanca', 'Kigali', 'Dar es Salaam', 'Addis Ababa', 'Abuja', 'Kampala'],
    regulations: [
      'Nigeria FIRS e-invoicing readiness (2024+ rollout)',
      'South Africa VAT (15%) handling for SARS filing',
      'Kenya KRA eTIMS e-invoicing readiness',
      'Egypt ETA e-invoicing readiness (bilingual Arabic/English)',
      'Multi-country VAT/sales tax configuration per branch',
    ],
    faqs: [
      {q: 'Does FlavorFeed work with M-Pesa in Kenya?', a: 'Yes — M-Pesa Paybill/Till integration via Flutterwave and Safaricom Daraja. Orders can be paid directly from M-Pesa with receipt linkage.'},
      {q: 'What if my internet drops — can I still take orders?', a: 'Yes — FlavorFeed has full offline mode. Orders, payments (cash/card pre-auth), and receipts all work offline and auto-sync the moment the internet returns. Critical for African markets with inconsistent connectivity.'},
      {q: 'Is FlavorFeed ready for Nigeria FIRS e-invoicing?', a: 'FlavorFeed is tracking the FIRS e-invoicing rollout and will deliver full compliance by the enforcement deadline. Basic VAT invoice compliance is already live.'},
      {q: 'Which African payment processors does FlavorFeed support?', a: 'Flutterwave, Paystack (Nigeria, Ghana, Kenya, South Africa), Stripe (where available), plus direct mobile money integration. Contact us for specific country roadmap.'},
    ],
  },
};
